import {
  Children,
  cloneElement,
  isValidElement,
  Fragment,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../utils/cn';

export interface FormFieldProps {
  readonly label?: ReactNode;
  readonly helper?: ReactNode;
  /** Error message — when non-empty, the field is marked invalid. */
  readonly error?: ReactNode;
  readonly required?: boolean;
  readonly htmlFor?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

interface AugmentableProps {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-required'?: boolean;
  'aria-labelledby'?: string;
}

/**
 * Form field wrapper — label · helper · error.
 *
 * Auto-wires `id` / `aria-describedby` / `aria-invalid` / `aria-required` on
 * the single child input element when it doesn't already define them. Pass
 * `htmlFor` to force a fixed id instead of useId(). When the child already
 * supplies an `id`, the label points at that id.
 */
export function FormField({
  label,
  helper,
  error,
  required = false,
  htmlFor,
  className,
  children,
}: FormFieldProps) {
  const generatedId = useId();
  const fallbackId = htmlFor ?? generatedId;
  const labelId = `${generatedId}-label`;
  const helperId = helper && !error ? `${fallbackId}-helper` : undefined;
  const errorId = error ? `${fallbackId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  let augmented: ReactNode = children;
  let resolvedId = fallbackId;
  const arr = Children.toArray(children);
  if (arr.length === 1 && isValidElement(arr[0]) && arr[0].type !== Fragment) {
    const child = arr[0] as ReactElement<AugmentableProps>;
    resolvedId = child.props.id ?? fallbackId;
    const patch: AugmentableProps = {};
    if (child.props.id === undefined) patch.id = fallbackId;
    if (describedBy !== undefined) {
      patch['aria-describedby'] = [child.props['aria-describedby'], describedBy]
        .filter(Boolean)
        .join(' ');
    }
    if (label && child.props['aria-labelledby'] === undefined) patch['aria-labelledby'] = labelId;
    if (child.props['aria-invalid'] === undefined && error) {
      patch['aria-invalid'] = true;
    }
    if (child.props['aria-required'] === undefined && required) {
      patch['aria-required'] = true;
    }
    augmented = cloneElement(child, patch);
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <label
          id={labelId}
          htmlFor={resolvedId}
          className="text-small font-semibold text-ink-2 leading-snug"
          onClick={() => {
            const target = document.getElementById(resolvedId);
            if (
              !target ||
              target.matches('input, select, textarea, button, meter, output, progress')
            )
              return;
            const focusable = target.querySelector<HTMLElement>(
              '[tabindex="0"], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)',
            );
            (focusable ?? target).focus();
          }}
        >
          {label}
          {required ? (
            <span className="ms-1 text-danger" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {augmented}
      {helper && !error ? (
        <p id={helperId} className="text-small text-ink-2 leading-snug">
          {helper}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-[11.5px] text-danger leading-snug">
          {error}
        </p>
      ) : null}
    </div>
  );
}
